import { useTranslate } from '@ur-solutions/react-hooks'
import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  isAfter,
  startOfDay,
  isSameDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { Activity, useMemo } from 'react'
import { bitmaskToWeekdays } from '@/utils'
import type { AbsenceLockForm } from './absence-lock-edit'
import type { RRuleWeekday } from '../util'

export type AbsenceLockRuleForm = AbsenceLockForm['configRules'][number]

interface LockWindow {
  start: Date
  end: Date
  ruleIndex: number
  frequency: AbsenceLockRuleForm['frequency']
}

interface MergedWindow {
  start: Date
  end: Date
  ruleIndexes: number[]
}

const WEEKDAY_INDEX: Record<RRuleWeekday, number> = {
  MO: 0,
  TU: 1,
  WE: 2,
  TH: 3,
  FR: 4,
  SA: 5,
  SU: 6,
}

function resolveStartDay(date: Date, startDay: number) {
  if (!startDay) return null
  const daysInMonth = endOfMonth(date).getDate()

  if (startDay > 0) {
    if (startDay > daysInMonth) return null
    return startDay
  }

  const day = daysInMonth + 1 + startDay
  if (day < 1 || day > daysInMonth) return null
  return day
}

function getWeeklyOccurrences(
  rule: AbsenceLockRuleForm,
  count: number,
  today: Date
) {
  const selectedWeekdays = bitmaskToWeekdays(rule.byWeekdays ?? 0)
  if (!selectedWeekdays.length) return []

  const anchorDate = rule.interval > 1 ? rule.startDate : today
  const minStartDate = rule.interval > 1 ? startOfDay(rule.startDate) : null
  const initialWeekStart = startOfWeek(anchorDate, { weekStartsOn: 1 })
  const weekdays = [...new Set(selectedWeekdays)]
    .map(day => WEEKDAY_INDEX[day])
    .sort((a, b) => a - b)
  const hasEligibleWeekday = weekdays.some(weekday => {
    const start = addDays(initialWeekStart, weekday)
    return !minStartDate || start >= minStartDate
  })
  const weekStart = hasEligibleWeekday
    ? initialWeekStart
    : addDays(initialWeekStart, 7)

  const occurrences: Array<{ start: Date; end: Date }> = []
  const maxWeeks = 260

  for (let weekIndex = 0; weekIndex < maxWeeks; weekIndex += 1) {
    if (weekIndex % rule.interval !== 0) continue

    for (const weekday of weekdays) {
      const start = addDays(weekStart, weekIndex * 7 + weekday)
      const end = addDays(start, rule.durationDays - 1)

      if (minStartDate && start < minStartDate) continue
      if (isAfter(today, end)) continue

      occurrences.push({ start, end })
    }

    if (occurrences.length >= count) break
  }

  return occurrences
}

function getMonthlyOccurrences(
  rule: AbsenceLockRuleForm,
  count: number,
  today: Date
) {
  const minStartDate = rule.interval > 1 ? startOfDay(rule.startDate) : null
  let anchor = startOfMonth(rule.interval > 1 ? rule.startDate : today)

  if (rule.interval > 1 && minStartDate) {
    const maxShift = 24
    for (let shift = 0; shift < maxShift; shift += 1) {
      const day = resolveStartDay(anchor, rule.startDay)
      if (day) {
        const candidate = new Date(
          anchor.getFullYear(),
          anchor.getMonth(),
          day
        )
        if (candidate >= minStartDate) break
      }
      anchor = addMonths(anchor, 1)
    }
  }
  const occurrences: Array<{ start: Date; end: Date }> = []
  const maxMonths = 240

  for (let monthIndex = 0; monthIndex < maxMonths; monthIndex += 1) {
    if (monthIndex % rule.interval !== 0) continue

    const monthDate = addMonths(anchor, monthIndex)
    const day = resolveStartDay(monthDate, rule.startDay)
    if (!day) continue

    const start = new Date(monthDate.getFullYear(), monthDate.getMonth(), day)
    const end = addDays(start, rule.durationDays - 1)

    if (minStartDate && start < minStartDate) continue
    if (isAfter(today, end)) continue

    occurrences.push({ start, end })
    if (occurrences.length >= count) return occurrences
  }

  return occurrences
}

function getYearlyOccurrences(
  rule: AbsenceLockRuleForm,
  count: number,
  today: Date
) {
  const minStartDate = rule.interval > 1 ? startOfDay(rule.startDate) : null
  let year = (rule.interval > 1 ? rule.startDate : today).getFullYear()

  if (rule.interval > 1 && minStartDate) {
    const maxShift = 10
    for (let shift = 0; shift < maxShift; shift += 1) {
      const candidate = new Date(year + shift, rule.month, 1)
      const day = resolveStartDay(candidate, rule.startDay)
      if (day) {
        const start = new Date(year + shift, rule.month, day)
        if (start >= minStartDate) {
          year = year + shift
          break
        }
      }
    }
  }
  const occurrences: Array<{ start: Date; end: Date }> = []
  const maxYears = 30

  for (let yearIndex = 0; yearIndex < maxYears; yearIndex += 1) {
    if (yearIndex % rule.interval !== 0) continue

    const candidate = new Date(year + yearIndex, rule.month, 1)
    const day = resolveStartDay(candidate, rule.startDay)
    if (!day) continue

    const start = new Date(year + yearIndex, rule.month, day)
    const end = addDays(start, rule.durationDays - 1)

    if (minStartDate && start < minStartDate) continue
    if (isAfter(today, end)) continue

    occurrences.push({ start, end })
    if (occurrences.length >= count) return occurrences
  }

  return occurrences
}

function mergeLockWindows(windows: LockWindow[]) {
  const sorted = [...windows].sort(
    (a, b) => a.start.getTime() - b.start.getTime()
  )
  if (!sorted[0]) return []

  const merged: MergedWindow[] = []

  let current: MergedWindow = {
    start: sorted[0].start,
    end: sorted[0].end,
    ruleIndexes: [sorted[0].ruleIndex],
  }

  for (const next of sorted.slice(1)) {
    if (next.start <= addDays(current.end, 1)) {
      if (next.end > current.end) current.end = next.end
      if (!current.ruleIndexes.includes(next.ruleIndex))
        current.ruleIndexes.push(next.ruleIndex)
      continue
    }

    merged.push(current)
    current = {
      start: next.start,
      end: next.end,
      ruleIndexes: [next.ruleIndex],
    }
  }

  merged.push(current)
  return merged
}

function getUpcomingLocks(rules: AbsenceLockRuleForm[], count: number) {
  const today = new Date()
  const windows: LockWindow[] = []

  const perRuleCount = Math.max(6, count * 3)

  rules.forEach((rule, index) => {
    let occurrences: Array<{ start: Date; end: Date }> = []
    if (rule.frequency === 'WEEKLY')
      occurrences = getWeeklyOccurrences(rule, perRuleCount, today)
    if (rule.frequency === 'MONTHLY')
      occurrences = getMonthlyOccurrences(rule, perRuleCount, today)
    if (rule.frequency === 'YEARLY')
      occurrences = getYearlyOccurrences(rule, perRuleCount, today)

    occurrences.forEach(window => {
      windows.push({
        start: window.start,
        end: window.end,
        ruleIndex: index,
        frequency: rule.frequency,
      })
    })
  })

  return mergeLockWindows(windows)
    .sort((a, b) => a.start.getTime() - b.start.getTime())
    .slice(0, count)
}

function formatWindowRange(start: Date, end: Date) {
  if (isSameDay(start, end)) return format(start, 'PP')
  return `${format(start, 'PP')} – ${format(end, 'PP')}`
}

interface Props {
  rules: AbsenceLockRuleForm[]
}

export function AbsenceLockUpcoming({ rules }: Props) {
  const translations = useTranslate({
    title: 'absences.absence-lock.form.preview.title',
    empty: 'absences.absence-lock.form.preview.empty',
    rulePrefix: 'absences.absence-lock.form.preview.rule-prefix',
  })

  const upcomingLocks = useMemo(() => getUpcomingLocks(rules, 5), [rules])

  return (
    <div className="mt-6 rounded-lg border border-border bg-background p-4">
      <h3 className="text-lg font-semibold">{translations.title}</h3>

      <Activity mode={!upcomingLocks.length ? 'visible' : 'hidden'}>
        <p className="text-sm text-muted-foreground mt-2">
          {translations.empty}
        </p>
      </Activity>

      <Activity mode={!!upcomingLocks.length ? 'visible' : 'hidden'}>
        <div className="mt-3 flex flex-col gap-2">
          {upcomingLocks.map(lock => (
            <div
              key={`${lock.ruleIndexes.join('-')}-${lock.start.toISOString()}`}
              className="flex items-center justify-between rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm"
            >
              <span className="font-medium">
                {lock.ruleIndexes
                  .slice()
                  .sort((a, b) => a - b)
                  .map(
                    ruleIndex => `${translations.rulePrefix} ${ruleIndex + 1}`
                  )
                  .join(' / ')}
              </span>
              <span>{formatWindowRange(lock.start, lock.end)}</span>
            </div>
          ))}
        </div>
      </Activity>
    </div>
  )
}
